import axios from "axios";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import { BsUpcScan } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import aluminumCan from "../../assets/aluminum-can.png";
import glassBottle from "../../assets/glass_bottle.png";
import plasticBottle from "../../assets/plastic_water_bottle.png";

export default function ScanPickup() {
  const navigate = useNavigate();
  const { pickup_id } = useParams();
  const lastInvalidRef = useRef(null);

  const [pickup, setPickup] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [expectedBarcodes, setExpectedBarcodes] = useState([]);
  const [scannedBarcodes, setScannedBarcodes] = useState([]);
  const scannedBarcodesRef = useRef([]);
  const [rcId, setRcId] = useState(""); // RC ID
  const [driverId, setDriverId] = useState(""); // Driver ID

  const [bagSelections, setBagSelections] = useState({});
  const html5QrCodeRef = useRef(null);

  const [manualBarcode, setManualBarcode] = useState("");

  useEffect(() => {
    const fetchPickup = async () => {
      try {
        const res = await axios.get(
          `/auth/scan-pickup-history/${pickup_id}`
        );
        const data = res.data;
        setPickup(data);

        // ✅ Set RC and Driver IDs
        setRcId(data.rc_id || ""); // make sure backend returns rc_id
        setDriverId(data.driver_assigned || ""); // driver code

        if (data.barcode) {
          const list = data.barcode
            .split(",")
            .map((b) => b.trim())
            .filter((b) => b !== "");
          setExpectedBarcodes(list);

          if (data.pickup_status === "completed") {
            scannedBarcodesRef.current = list;
            setScannedBarcodes(list);

            const bagMap = {};
            if (data.bags && data.bags.length > 0) {
              data.bags.forEach((b) => {
                bagMap[b.barcode] = b.items_count;
              });
            }
            setBagSelections(bagMap);
          }
        }
      } catch (err) {
        console.error("Error fetching pickup", err);
        toast.error("Failed to load pickup data.");
      }
    };

    fetchPickup();
  }, [pickup_id]);


  const handleBagSelection = (barcode, count) => {
    const total =
      (count.plastic || 0) +
      (count.aluminum || 0) +
      (count.glass || 0);

    if (total > 240) {
      toast.error("Bag total must be 240 items or fewer.");
      return;
    }

    setBagSelections((prev) => ({
      ...prev,
      [barcode]: count,
    }));
  };

  const handleSubmitPickupCompletion = async () => {
    for (const code of scannedBarcodes) {
      const bag = bagSelections[code];

      if (!bag) {
        toast.error("Please enter counts for all scanned bags.");
        return;
      }

      const plastic = Number(bag.plastic || 0);
      const aluminum = Number(bag.aluminum || 0);
      const glass = Number(bag.glass || 0);
      const total = plastic + aluminum + glass;

      if (total < 1) {
        toast.error("Bag item total must be at least 1.");
        return;
      }

      if (total > 240) {
        toast.error("Bag total must be 240 items or fewer.");
        return;
      }
    }

    // 🔹 Calculate amounts
    let grossAmount = 0;
    let serviceFeeTotal = 0;
    let handlingTotal = 0;
    let farefuelMarginTotal = 0;
    let rcPayTotal = 0;
    let driverPayTotal = 0;

    const bags = scannedBarcodes.map((code) => {
      const { plastic = 0, aluminum = 0, glass = 0 } = bagSelections[code] || {};
      const totalItems = Math.min(240, plastic + aluminum + glass);

      const grossRate = pickup?.gross_per_item ?? 0.05;
      const handlingRate = pickup?.handling_per_item ?? 0.035;

      const gross = +(totalItems * grossRate).toFixed(4);   // ✅ ADD THIS LINE

      const serviceFee = +(gross * 0.10).toFixed(4);

      const handlingReimbursed = +(totalItems * handlingRate).toFixed(4);

      const farefuelMargin = Math.round((serviceFee + handlingReimbursed) * 100) / 100;
      const net = gross - serviceFee;

      // ✅ RC & Driver Pay
      const rcSplit = pickup?.rc_split_percent ?? 55; // dynamic if fetched, default 55
      const driverSplit = 100 - rcSplit;              // complementary split

      const rc_pay = +(gross * (rcSplit / 100)).toFixed(4);
      const driver_pay = +(gross * (driverSplit / 100)).toFixed(4);

      grossAmount += gross;
      serviceFeeTotal += serviceFee;
      handlingTotal += handlingReimbursed;
      farefuelMarginTotal += farefuelMargin;
      rcPayTotal += rc_pay;
      driverPayTotal += driver_pay;

      return {
        barcode: code,
        plastic_count: plastic,
        aluminum_count: aluminum,
        glass_count: glass,
        items_count: totalItems,
        gross_amount: gross.toFixed(2),
        service_fee: serviceFee.toFixed(2),
        handling_reimbursed: handlingReimbursed.toFixed(2),
        farefuel_margin: farefuelMargin.toFixed(2),
        net_amount: net.toFixed(2),
        rc_pay: rc_pay.toFixed(2),
        driver_pay: driver_pay.toFixed(2),
      };
    });

    const netAmount = grossAmount - serviceFeeTotal;

    try {
      await axios.post("/auth/complete-pickup", {
        pickup_id,
        rc_id: rcId,         // ✅ Add this line
        driver_id: driverId, // ✅ Add this line
        bags,
        gross_total: grossAmount.toFixed(2),
        service_fee_total: serviceFeeTotal.toFixed(2),
        handling_total: handlingTotal.toFixed(2),
        farefuel_margin_total: farefuelMarginTotal.toFixed(2),
        net_total: netAmount.toFixed(2),
        rc_pay_total: rcPayTotal.toFixed(2),
        driver_pay_total: driverPayTotal.toFixed(2),
      });


      await axios.post("/auth/update-barcode-status", {
        scannedBarcodes,
      });

      toast.success("Pickup marked as completed and barcodes updated");
      setPickup((prev) => ({
        ...prev,
        pickup_status: "completed",
        amount: netAmount,
        bags,
      }));
      setBagSelections({ ...bagSelections });
    } catch (err) {
      console.error("Error during pickup completion:", err?.response?.data || err.message);
      toast.error(`Failed: ${err?.response?.data?.message || err.message}`);
    }
  };

  const startScanner = async () => {
    if (scanning || pickup?.pickup_status === "completed") return;
    setScanning(true);

    const config = {
      fps: 30,
      qrbox: { width: 300, height: 300 },
      formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128],
    };

    if (html5QrCodeRef.current) {
      await stopScanner();
      await new Promise((res) => setTimeout(res, 300));
    }

    const html5QrCode = new Html5Qrcode("html5qr-code-full-region");
    html5QrCodeRef.current = html5QrCode;

    try {
      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        async (scannedText) => {
          handleScannedCode(scannedText.trim());
        },
        () => { }
      );
    } catch (err) {
      toast.error("Failed to start camera.");
      console.error("html5-qrcode start error:", err);
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.warn("Error while stopping/clearing the scanner:", err);
      } finally {
        html5QrCodeRef.current = null;
      }
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const handleScannedCode = (scanned) => {
    if (pickup?.pickup_status === "completed") {
      stopScanner();
      return;
    }

    if (scannedBarcodesRef.current.includes(scanned)) {
      toast.info("Barcode already scanned.");
      return;
    }

    if (!expectedBarcodes.includes(scanned)) {
      if (lastInvalidRef.current !== scanned) {
        toast.error(`Invalid barcode: ${scanned}`);
        lastInvalidRef.current = scanned;
      }
      return;
    }

    lastInvalidRef.current = null;
    scannedBarcodesRef.current.push(scanned);
    setScannedBarcodes([...scannedBarcodesRef.current]);
    toast.success(`Verified: ${scanned}`);
    stopScanner();

    if (scannedBarcodesRef.current.length === expectedBarcodes.length) {
      toast.info("All barcodes scanned. Please enter item counts.");
    }
  };

  const handleManualSubmit = () => {
    const trimmed = manualBarcode.trim();
    if (!trimmed) {
      toast.error("Enter a barcode first.");
      return;
    }
    handleScannedCode(trimmed);
    setManualBarcode("");
  };

  if (!pickup)
    return <div className="text-center p-6">Loading pickup data...</div>;

  const isCompleted = pickup.pickup_status === "completed";

  // 🔹 Dynamic calculation for display
  let grossAmount = 0;
  let serviceFee = 0;
  let handlingTotal = 0;
  let farefuelMarginTotal = 0;

  Object.values(bagSelections).forEach((bag) => {
    const plastic = parseInt(bag.plastic) || 0;
    const aluminum = parseInt(bag.aluminum) || 0;
    const glass = parseInt(bag.glass) || 0;

    const totalItems = Math.min(240, plastic + aluminum + glass);

    const grossRate = pickup?.gross_per_item ?? 0.05;
    const handlingRate = pickup?.handling_per_item ?? 0.035;


    const gross = +(totalItems * grossRate).toFixed(4);

    const sf = +(gross * 0.10).toFixed(4);
    const hr = +(totalItems * handlingRate).toFixed(4);

    const ff = +(sf + hr).toFixed(4); // farefuel margin (service fee + handling)



    grossAmount += gross;
    serviceFee += sf;
    handlingTotal += hr;
    farefuelMarginTotal += ff;
  });

  const netAmount = grossAmount - serviceFee;

  return (
    <section className="py-10 flex justify-center items-start min-h-screen bg-gradient-to-b from-lime-100 to-white">
      <div className="bg-white shadow-lg rounded-xl p-8 w-full max-w-5xl flex flex-col items-center">
        <div className="flex items-center justify-center gap-2 text-2xl font-bold mb-6">
          <BsUpcScan className="text-3xl" />
          <span>Scan Pickup</span>
        </div>
        {/* <div className="mt-4 bg-blue-100 p-3 rounded-lg shadow">
  <div><b>Refund Per Item:</b> ${pickup.gross_per_item}</div>
  <div><b>Handling Per Item:</b> ${pickup.handling_per_item}</div>
  <div><b>RC Split:</b> {pickup.rc_split_percent}%</div>
  <div><b>Driver Split:</b> {pickup.driver_split_percent}%</div>
</div> */}

        {/* Scanner + Manual Input */}
        {!isCompleted && (
          <>
            <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:items-center">
              <button
                type="button"
                onClick={startScanner}
                className="bg-green-600 text-white px-4 py-2 rounded-md w-full sm:w-auto"
                disabled={scanning || scannedBarcodes.length === expectedBarcodes.length}
              >
                {scanning
                  ? "Scanning..."
                  : scannedBarcodes.length === 0
                    ? "Start Scanning"
                    : "Scan Next"}
              </button>

              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <input
                  type="text"
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  placeholder="Enter barcode manually"
                  className="border px-3 py-2 rounded-md w-full sm:w-64"
                  disabled={isCompleted}
                />
                <button
                  onClick={handleManualSubmit}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md w-full sm:w-auto"
                  disabled={isCompleted}
                >
                  Add
                </button>
              </div>
            </div>

            <div
              id="html5qr-code-full-region"
              className="w-full max-w-md aspect-video border border-gray-300 rounded-lg mb-4 relative overflow-hidden"
            ></div>

            {!scanning && (
              <button
                onClick={startScanner}
                className="bg-yellow-500 text-white px-4 py-2 rounded-md w-full sm:w-auto"
              >
                Try Again
              </button>
            )}
          </>
        )}
        {/* RC & Driver Pay Display */}
        {/* <div className="mt-6 bg-gray-100 p-4 rounded-lg shadow-sm w-full max-w-xl">
  <h3 className="text-lg font-bold mb-2 text-center">Pay Split</h3>
  <div className="flex justify-between text-gray-700 mb-1">
    <span>RC Split:</span>
    <span className={pickup.rc_split_percent ? "text-green-600" : "text-red-600"}>
      {pickup.rc_split_percent ?? 55}% {pickup.rc_split_percent ? "(fetched)" : "(default)"}
    </span>
  </div>
  <div className="flex justify-between text-gray-700">
    <span>Driver Split:</span>
    <span className={pickup.driver_split_percent ? "text-green-600" : "text-red-600"}>
      {pickup.driver_split_percent ?? 45}% {pickup.driver_split_percent ? "(fetched)" : "(default)"}
    </span>
  </div>

  <div className="mt-4 text-gray-800 font-medium">
    <div>RC Pay Total: $
      {scannedBarcodes.length > 0
        ? scannedBarcodes.reduce((total, code) => {
            const bag = bagSelections[code] || {};
            const totalItems = (bag.plastic || 0) + (bag.aluminum || 0) + (bag.glass || 0);
            const gross = totalItems * 0.05;
            const rcSplit = pickup.rc_split_percent ?? 55;
            return total + gross * (rcSplit / 100);
          }, 0).toFixed(2)
        : "0.00"
      }
    </div>

    <div>Driver Pay Total: $
      {scannedBarcodes.length > 0
        ? scannedBarcodes.reduce((total, code) => {
            const bag = bagSelections[code] || {};
            const totalItems = (bag.plastic || 0) + (bag.aluminum || 0) + (bag.glass || 0);
            const gross = totalItems * 0.05;
            const driverSplit = pickup.driver_split_percent ?? 45;
            return total + gross * (driverSplit / 100);
          }, 0).toFixed(2)
        : "0.00"
      }
    </div>
  </div>
</div> */}

        {/* Bag Input Section */}
        {(scannedBarcodes.length > 0 || isCompleted) && (
          <div className="mt-6 w-full max-w-xl bg-lime-200 p-4 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold mb-4 text-center">Bag Details</h3>

            {scannedBarcodes.map((code) => (
              <div key={code} className="mb-6 border-b border-gray-300 pb-4">
                <div className="font-semibold mb-3 text-center text-gray-800">{code}</div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-white p-2 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2">
                      <img src={plasticBottle} alt="Plastic Bottles" className="w-8 h-8" />
                      <span className="font-medium text-gray-800">Plastic Bottles</span>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max="240"
                      value={bagSelections[code]?.plastic || ""}
                      onChange={(e) => {
                        const value = Math.max(0, parseInt(e.target.value, 10) || 0);
                        handleBagSelection(code, {
                          ...bagSelections[code],
                          plastic: value,
                        });
                      }}
                      disabled={isCompleted}
                      className="border border-gray-300 rounded-md px-3 py-1 w-24 text-center"
                      placeholder="Items"
                    />
                  </div>

                  <div className="flex items-center justify-between bg-white p-2 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2">
                      <img src={aluminumCan} alt="Aluminum Cans" className="w-8 h-8" />
                      <span className="font-medium text-gray-800">Aluminum Cans</span>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max="240"
                      value={bagSelections[code]?.aluminum || ""}
                      onChange={(e) => {
                        const value = Math.max(0, parseInt(e.target.value, 10) || 0);
                        handleBagSelection(code, {
                          ...bagSelections[code],
                          aluminum: value,
                        });
                      }}
                      disabled={isCompleted}
                      className="border border-gray-300 rounded-md px-3 py-1 w-24 text-center"
                      placeholder="Items"
                    />
                  </div>

                  <div className="flex items-center justify-between bg-white p-2 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2">
                      <img src={glassBottle} alt="Glass Bottles" className="w-8 h-8" />
                      <span className="font-medium text-gray-800">Glass Bottles</span>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max="240"
                      value={bagSelections[code]?.glass || ""}
                      onChange={(e) => {
                        const value = Math.max(0, parseInt(e.target.value, 10) || 0);
                        handleBagSelection(code, {
                          ...bagSelections[code],
                          glass: value,
                        });
                      }}
                      disabled={isCompleted}
                      className="border border-gray-300 rounded-md px-3 py-1 w-24 text-center"
                      placeholder="Items"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-4 text-lg font-semibold">
              Gross Amount: ${isCompleted ? pickup.gross_total : grossAmount.toFixed(2)}
            </div>
            <div className="text-lg font-semibold text-red-600">
              Service Fee (10%): -${isCompleted ? pickup.service_fee_total : serviceFee.toFixed(2)}
            </div>
            <div className="text-xl font-bold mt-2">
              Net Amount: ${isCompleted ? pickup.net_total : netAmount.toFixed(2)}
            </div>

            {!isCompleted && scannedBarcodes.length === expectedBarcodes.length && (
              <button
                onClick={handleSubmitPickupCompletion}
                className="mt-4 px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white w-full"
              >
                Complete Pickup
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-gray-700 w-full mt-10 max-w-3xl">
          <div><span className="font-bold">Request ID:</span> {pickup.req_id}</div>
          <div><span className="font-bold">Customer Id:</span> {pickup.cust_id}</div>

          <div><span className="font-bold">Barcode(s):</span> {pickup.barcode}</div>
          <div><span className="font-bold">Pickup Status:</span> {pickup.pickup_status}</div>
          <div><span className="font-bold">Payment method:</span> {pickup.payment_method}</div>
          <div><span className="font-bold">First Name:</span> {pickup.first_name}</div>
          <div><span className="font-bold">Last Name:</span> {pickup.last_name}</div>
          <div><span className="font-bold">Email:</span> {pickup.email}</div>
          <div><span className="font-bold">Street Address:</span> {pickup.street_address}</div>
          <div><span className="font-bold">City:</span> {pickup.city}</div>
          <div><span className="font-bold">State:</span> {pickup.state}</div>
          <div><span className="font-bold">Estimated Bags:</span> {pickup.estimated_bags}</div>
          <div><span className="font-bold">Pickup Date:</span> {new Date(pickup.pickup_date).toDateString()}</div>
          <div><span className="font-bold">Pickup Time:</span> {pickup.pickup_time}</div>
          <div className="sm:col-span-2">
            <span className="font-bold">Assigned to:</span>{" "}
            {pickup.driver_assigned || "Not yet assigned"}
          </div>
        </div>

        <div className="flex justify-end w-full mt-8">
          <button
            onClick={() => navigate("/DriverPickupHistory")}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
          >
            Back
          </button>
        </div>

        <ToastContainer position="top-right" autoClose={2500} />
      </div>
    </section>
  );
}
